import { useEffect, useRef, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { TooltipProvider } from '@/components/ui/tooltip'
import { Sidebar } from './Sidebar'
import { Header } from './Header'
import type { ShellContext } from './shellContext'
import { SearchCommandPalette } from './SearchCommandPalette'
import { TourProvider } from '@/components/tour/TourProvider'
import { ChatPanel } from './ChatPanel'
import { DebugTracePanel } from '@/components/debug/DebugTracePanel'
import { UploadTray } from '@/components/media/UploadTray'
import { FeedbackHost } from '@/components/feedback/FeedbackHost'
import { useUiStore } from '@/stores/useUiStore'
import { consumeSwitchedBusinessName } from '@/stores/useMyBusinessStore'
import { useBillingStore } from '@/stores/useBillingStore'
import { useJobsStore } from '@/stores/useJobsStore'
import { useChatContext } from '@/hooks/useChatContext'
import { useIsMobile } from '@/hooks/useMediaQuery'
import { useEventStream } from '@/hooks/useEventStream'
import { useDocumentTitle } from '@/components/layout/breadcrumbs'
import { useShellPolling } from '@/hooks/useShellPolling'
import { usePushNotificationRouting } from '@/hooks/usePushNotificationRouting'
import { useContextualPushPrompt } from '@/hooks/useContextualPushPrompt'
import { useAppOpenWake } from '@/hooks/useAppOpenWake'

export function AppShell() {
  // Synchronize chat context with current route and selected items
  useChatContext()
  useDocumentTitle()

  // Live update delivery: refreshes the bell + jobs store on every
  // Notification row, and invalidates query caches on every domain event,
  // instead of waiting for the 60s poll.
  useEventStream()

  // Business list, budget alerts, notifications and jobs: loaded
  // once, then refreshed on a 60s safety cadence.
  useShellPolling()

  // Push-tap deep links + foreground refresh. No-op on web.
  usePushNotificationRouting()

  // Contextual push-permission nudge: offered after a qualifying
  // in-app event rather than at launch/login. No-op on web.
  useContextualPushPrompt()

  // Opening or coming back to the app wakes the business's agent once.
  useAppOpenWake()

  const [searchOpen, setSearchOpen] = useState(false)
  const isMobile = useIsMobile()
  const { pathname } = useLocation()
  const isChatRoute = pathname === '/chat' || pathname.startsWith('/chat/')
  const mainRef = useRef<HTMLElement>(null)
  const fetchCreditBalance = useBillingStore((state) => state.fetchCreditBalance)

  const loadJobs = useJobsStore((state) => state.loadJobs)

  // Load the business-wide jobs list on mount (survives page reload). After
  // that the SSE stream refreshes it (useEventStream), with a 60s safety
  // refetch in useShellPolling.
  useEffect(() => {
    loadJobs()
  }, [loadJobs])

  // Fetch credit balance on mount so the header indicator is populated
  useEffect(() => {
    fetchCreditBalance()
  }, [fetchCreditBalance])

  // Post-switch orientation: switchBusiness ends in a full reload, so
  // its confirmation rides sessionStorage across the boundary. Read-and-clear
  // on boot and confirm which business the user is now working in.
  useEffect(() => {
    const name = consumeSwitchedBusinessName()
    if (!name) return
    useUiStore.getState().showToast({
      type: 'success',
      title: 'Business switched',
      message: `You're now working in "${name}".`,
    })
  }, [])

  // Reset the content scroll on every route change — the scrollable element is
  // <main>, not window, so a fresh page would otherwise inherit the prior
  // scroll position (most visible on long course pages opened from a link far
  // down the previous page). Path-only: tab/query changes don't reset.
  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0, left: 0 })
  }, [pathname])

  return (
    <TooltipProvider>
      <TourProvider>
        {/* pt-[env(safe-area-inset-top)]: keeps Header/Sidebar clear of the notch
          when running standalone with viewport-fit=cover (inset is 0 in-browser) */}
        <div className="bg-background flex h-dvh w-full overflow-hidden pt-[env(safe-area-inset-top)]">
          {/* Desktop/Tablet sidebar - not mounted on mobile (< md), where the
              home screen (ChatPage) renders the one Sidebar instead. */}
          {!isMobile && (
            <div className="flex">
              <Sidebar onSearch={() => setSearchOpen(true)} />
            </div>
          )}

          {/* Main content area */}
          <div className="flex flex-1 flex-col overflow-hidden">
            {!isChatRoute && <Header />}
            {/* /chat is full-bleed: the chat surface draws its own top bar and
              manages its own scrolling, so drop the padding and let the page
              fill <main> edge-to-edge. */}
            <main
              ref={mainRef}
              className={cn(
                'flex-1',
                isChatRoute
                  ? 'flex min-h-0 flex-col overflow-hidden'
                  : 'overflow-auto p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] md:p-6'
              )}
            >
              <Outlet context={{ openSearch: () => setSearchOpen(true) } satisfies ShellContext} />
            </main>
          </div>

          {/* The Marketing side panel, opened from the page top bar */}
          <ChatPanel />
        </div>

        <UploadTray />

        <SearchCommandPalette open={searchOpen} onOpenChange={setSearchOpen} />

        {/* In-app feedback — no visible chrome of its own; opened from the Header user menu,
            the account menu, or Cmd/Ctrl+Shift+F. */}
        <FeedbackHost />

        {/* Debug trace panel - toggle with Ctrl+Shift+D (dev only) */}
        {import.meta.env.DEV && <DebugTracePanel />}
      </TourProvider>
    </TooltipProvider>
  )
}
